import React, { useEffect } from 'react';
import {
  BrowserRouter as Router, Navigate, Route, Routes
} from 'react-router-dom';
import './i18n';
import './styles/globals.scss';
import { batch, useDispatch } from 'react-redux';

import Dashboard from './pages/Dashboard/Dashboard';
import Error404 from './pages/404/Error404';
import ProtectedRoute from './router/ProtectRouter';
import Login from './pages/Auth/Login/Login';
import Registration from './pages/Auth/Registration/Registration';
import Profile from './pages/Profile/Profile';
import Roles from './pages/UserManagement/Roles/Roles';
import getMyProfile from './thunks/profile/getMyProfile';
import getPermissions from './thunks/permissions/getPermissions';
import RoleDetail from './pages/UserManagement/RoleDetail/RoleDetail';
import Users from './pages/UserManagement/Users/Users';
import UserDetails from './pages/UserManagement/UserDetails/UserDetails';
import routes from './router/routes';
import Logs from './pages/Logs/Logs';
import SupplierApiLogs from './pages/Logs/SupplierApiLogs';
import SupplierSettings from './pages/SupplierSettings/SupplierSettings';
import ServiceTokens from './pages/ServiceTokens/ServiceTokens';
import OrderedParts from './pages/Coilovers/OrderedParts/OrderedParts';
import Warehouse from './pages/Coilovers/Warehouse/Warehouse';
import Orders from './pages/Coilovers/Orders/Orders';
import Articles from './pages/Articles/Articles';
import ArticlesCreateEdit from './pages/ArticlesCreateEdit/ArticlesCreateEdit';
import ForgotPassword from './pages/Auth/ForgotPassword/ForgotPassword';
import ResetPassword from './pages/Auth/ResetPassword/ResetPassword';
import Compatibility from './pages/Compatibility/Compatibility';
import Acceptance from './pages/Coilovers/Acceptance/Acceptance';
import PrinterTasks from './pages/PrinterTasks/PrinterTasks';
import EbayTasks from './pages/EbayTasks/EbayTasks';
import { PusherProvider } from './providers/PusherProvider';
import PusherOnlineListener from './listeners/PusherOnlineListener';
import PusherOrderListener from './listeners/PusherOrderListener';
import JobInProgress from './pages/Jobs/JobsInProgress/JobInProgress';
import JobsFailed from './pages/Jobs/JobsFailed/JobsFailed';
import FailedImportRows from './pages/Jobs/FailedImportRows/FailedImportRows';
import ConnectionStatusMonitor from './components/ConnectionStatusMonitor/ConnectionStatusMonitor';

const App = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    batch(() => {
      dispatch(getPermissions());
      dispatch(getMyProfile());
    });
  }, []);

  return (
    <PusherProvider>
      <ConnectionStatusMonitor />
      <Router>
        <Routes>
          <Route path={routes.LOGIN} element={<Login />} />
          <Route path={routes.REGISTRATION} element={<Registration />} />
          <Route path={routes.FORGOT_PASSWORD} element={<ForgotPassword />} />
          <Route path={routes.FORGOT_RESET} element={<ResetPassword />} />

          <Route exact path="/" element={<ProtectedRoute />}>
            <Route path="/" element={<Navigate to={routes.LOGIN} />} />
            <Route exact path={routes.DASHBOARD} element={<Dashboard />} />
            <Route exact path={routes.PROFILE} element={<Profile />} />
            <Route path={routes.SERVICE_TOKENS} element={<ServiceTokens />} />
            <Route
              exact
              path={routes.ROLES}
              element={<Roles />}
            />
            <Route
              exact
              path={routes.ROLE_DETAILS}
              element={<RoleDetail />}
            />
            <Route
              exact
              path={routes.USERS}
              element={<Users />}
            />
            <Route
              exact
              path={routes.USER_DETAILS}
              element={<UserDetails />}
            />
            <Route
              exact
              path={routes.USER_LOGS}
              element={<Logs />}
            />
            <Route
              exact
              path={routes.SUPPLIER_API_LOGS}
              element={<SupplierApiLogs />}
            />
            <Route
              exact
              path={routes.SUPPLIER_SETTINGS}
              element={<SupplierSettings />}
            />
            <Route
              exact
              path={routes.ACCEPTANCE}
              element={<Acceptance />}
            />
            <Route
              exact
              path={routes.CO_ORDERED_PARTS}
              element={<OrderedParts />}
            />
            <Route
              exact
              path={routes.CO_WAREHOUSE}
              element={<Warehouse />}
            />
            <Route
              exact
              path={routes.CO_ORDERS}
              element={(
                <>
                  <PusherOrderListener />
                  <Orders />
                </>
            )}
            />
            <Route
              exact
              path={routes.ARTICLES}
              element={<Articles />}
            />
            <Route
              exact
              path={routes.ARTICLES_EDIT}
              element={<ArticlesCreateEdit />}
            />
            <Route
              exact
              path={routes.ARTICLES_NEW}
              element={<ArticlesCreateEdit />}
            />
            <Route
              exact
              path={routes.COMPATIBILITY}
              element={<Compatibility />}
            />
            <Route
              exact
              path={routes.PRINTER}
              element={<PrinterTasks />}
            />
            <Route
              exact
              path={routes.EBAY_TASKS}
              element={<EbayTasks />}
            />
            <Route
              exact
              path={routes.JOBS_IN_PROGRESS}
              element={<JobInProgress />}
            />
            <Route
              exact
              path={routes.JOBS_FAILED}
              element={<JobsFailed />}
            />
            <Route
              exact
              path={routes.JOBS_FAILED_IMPORT_ROWS}
              element={<FailedImportRows />}
            />
          </Route>
          <Route path="*" element={<Error404 />} />
        </Routes>
      </Router>
      <PusherOnlineListener />
    </PusherProvider>
  );
};

export default App;
